import { useMemo } from 'react';

import { capitalize } from 'lodash-es';

import { IBackendCartEntries, ICartEntry } from '@rbi-ctg/menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { IMenuObject } from 'state/menu/types';
import { useUIContext } from 'state/ui';
import { isDummyCartEntry } from 'utils/cart';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { maybeMapCartEntryToEntryTypeAndId } from 'utils/menu';

interface UiPatternOption {
  _id?: string;
  uiPattern?: ComboSlotUiPattern | null;
}
interface UiPatternOptionMap {
  [id: string]: UiPatternOption;
}

interface ItemDescription {
  name: string;
  quantity: number;
  price: number;
}

type ItemDescriptionMap = { [key: string]: ItemDescription };

const getDescriptionMap = ({
  cartEntries = [],
  quantity,
  itemDescriptionMap,
  shouldHideCartEntry,
  shouldIncludeModifiers,
  computePrice,
}: {
  cartEntries?: ICartEntry['children'] | IBackendCartEntries['children'];
  quantity: number;
  itemDescriptionMap: ItemDescriptionMap;
  shouldHideCartEntry: (c: ICartEntry | IBackendCartEntries) => boolean;
  shouldIncludeModifiers: (c: ICartEntry | IBackendCartEntries) => boolean;
  computePrice: (c: ICartEntry | IBackendCartEntries) => number;
}): ItemDescriptionMap => {
  for (const cartEntry of cartEntries) {
    if (shouldHideCartEntry(cartEntry)) {
      continue;
    }

    if (shouldIncludeModifiers(cartEntry)) {
      const itemName = (cartEntry.name || '').split(' ').map(capitalize).join(' ');
      const price = computePrice(cartEntry);
      const quantityToAdd = quantity * cartEntry.quantity;

      if (itemDescriptionMap[itemName]) {
        // Add up if existing
        itemDescriptionMap[itemName] = {
          ...itemDescriptionMap[itemName],
          quantity: itemDescriptionMap[itemName].quantity + quantityToAdd,
          price: itemDescriptionMap[itemName].price + price,
        };
      } else {
        // Create new entry if empty
        itemDescriptionMap[itemName] = {
          name: itemName,
          quantity: quantityToAdd,
          price,
        };
      }
    }

    // Recursively add children
    itemDescriptionMap = {
      ...itemDescriptionMap,
      ...getDescriptionMap({
        cartEntries: cartEntry.children,
        quantity,
        itemDescriptionMap,
        shouldHideCartEntry,
        shouldIncludeModifiers,
        computePrice,
      }),
    };
  }
  return itemDescriptionMap;
};

/**
 * This hook takes a cart entry item and returns description
 * composed of comboSlot & itemOption selections.
 *
 * Note: includeModifiers also controls if we show comboSlot items.
 */
export function useComposeDescription(
  item: ICartEntry | IBackendCartEntries,
  options: {
    includeModifiers?: boolean;
    includeQuantity?: boolean;
  } = {}
): string[] {
  const { includeModifiers = false, includeQuantity = false } = options;

  const { sanityId, menuObjectType } = maybeMapCartEntryToEntryTypeAndId(item);
  const { data: menuObject } = useGetMenuObjectQuery({ menuObjectType, sanityId });
  const { formatCurrencyForLocale } = useUIContext();

  return useMemo(() => {
    // Function to compute Pricing
    const quantity = item.quantity;
    const itemPricesToIgnore = getMenuObjectPricesToIgnore(menuObject);
    const computePrice = (cartEntry: ICartEntry | IBackendCartEntries): number => {
      const shouldIgnorePrice = itemPricesToIgnore.has(cartEntry.sanityId);
      return shouldIgnorePrice ? 0 : (cartEntry.price || 0) * quantity;
    };

    // Function to determine Visibility
    const uiPatternMap = getMenuObjectUiPatternMap(menuObject);
    const shouldHideCartEntry = (cartEntry: ICartEntry | IBackendCartEntries): boolean => {
      const uiPattern =
        ('uiPattern' in cartEntry ? cartEntry.uiPattern : null) || uiPatternMap[cartEntry.sanityId];

      // Dummy items (usually combo slot markers or cartEntry markers) should be hidden
      if (cartEntry.isDummyItem || isDummyCartEntry(cartEntry)) {
        return true;
      }

      return (
        (cartEntry.type === CartEntryType.comboSlot && uiPattern === ComboSlotUiPattern.HIDDEN) ||
        uiPattern === ComboSlotUiPattern.COLLAPSED
      );
    };

    // Function to determine Modifiers criteria
    const shouldIncludeModifiers = (cartEntry: ICartEntry | IBackendCartEntries): boolean => {
      return (
        cartEntry.type === CartEntryType.item ||
        (includeModifiers && cartEntry.type === CartEntryType.itemOptionModifier)
      );
    };

    // Init recursion
    const itemDescriptionMap = getDescriptionMap({
      cartEntries: item.children || [],
      quantity,
      itemDescriptionMap: {},
      shouldHideCartEntry,
      shouldIncludeModifiers,
      computePrice,
    });

    // Format results
    return Object.values(itemDescriptionMap).map(itemDescription => {
      const quantityLabel = includeQuantity ? itemDescription.quantity : '';
      const nameLabel = itemDescription.name;
      const priceLabel =
        itemDescription.price !== 0 ? formatCurrencyForLocale(itemDescription.price) : '';
      const priceSign = priceLabel && itemDescription.price > 0 ? '+' : '';

      return `${quantityLabel} ${nameLabel} ${priceSign}${priceLabel}`;
    });
  }, [
    formatCurrencyForLocale,
    includeModifiers,
    includeQuantity,
    item.children,
    item.quantity,
    menuObject,
  ]);
}

const getMenuObjectPricesToIgnore = (menuObject: IMenuObject | null | undefined) => {
  const pricesToIgnore = new Set<string>();
  if (!menuObject) {
    return pricesToIgnore;
  }

  const mergedOptions = [
    ...(menuObject?.options || []),
    ...(('mainItem' in menuObject && menuObject?.mainItem?.options) || []),
  ];
  mergedOptions.forEach(option => {
    if (option && 'allowMultipleSelections' in option && Boolean(option.allowMultipleSelections)) {
      (option.options ?? []).forEach(opt => {
        if (opt?._key) {
          pricesToIgnore.add(opt._key);
        }
      });
    }
  });
  return pricesToIgnore;
};

const getMenuObjectUiPatternMap = (menuObject: IMenuObject | null | undefined) => {
  const uiPatternMap = {} as UiPatternOptionMap;

  for (const option of menuObject?.options ?? []) {
    if (option && 'uiPattern' in option) {
      uiPatternMap[option._id] = {
        _id: option._id,
        uiPattern: option.uiPattern as UiPatternOption['uiPattern'],
      };
    }
  }

  return uiPatternMap;
};
